import type { paths } from '@/services/generated/api';

/**
 * Types taken from the server's OpenAPI spec rather than written by hand.
 *
 * Regenerate with `npm run api:types` after the server changes. If a field is
 * renamed on the server, every use of it here becomes a compile error - which is
 * the entire reason the spec exists.
 */
type OpsLogin = paths['/api/production/auth/login']['post'];
type OpsMe = paths['/api/production/auth/me']['get'];
type OpsRefresh = paths['/api/production/auth/refresh']['post'];

type DashboardGet = paths['/api/production/dashboard']['get'];
type LogsList = paths['/api/production/logs']['get'];
type CrashesList = paths['/api/production/crashes']['get'];
type CrashGroupsList = paths['/api/production/crashes/groups']['get'];
type MfaVerify = paths['/api/production/auth/mfa/verify']['post'];
type MfaStatusPath = paths['/api/production/auth/mfa']['get'];
type MfaEnroll = paths['/api/production/auth/mfa/enroll']['post'];
type SessionsList = paths['/api/production/auth/sessions']['get'];
type IssuesList = paths['/api/production/issues']['get'];
type IssueRaise = paths['/api/production/issues']['post'];
type IssueUpdate = paths['/api/production/issues/{issueId}']['patch'];
type VersionsList = paths['/api/production/versions']['get'];
type VersionPublish = paths['/api/production/versions']['post'];
type VersionUpdate = paths['/api/production/versions/{versionId}']['patch'];
type VersionAdoptionGet = paths['/api/production/versions/adoption']['get'];
type DeploymentsList = paths['/api/production/deployments']['get'];
type DeploymentStart = paths['/api/production/deployments']['post'];
type DeploymentAdvance = paths['/api/production/deployments/{deploymentId}']['patch'];
type OpsSettingsGet = paths['/api/production/settings']['get'];
type OpsSettingsPut = paths['/api/production/settings']['put'];

export type LoginRequest = OpsLogin['requestBody']['content']['application/json'];
export type LoginResponse = OpsLogin['responses'][200]['content']['application/json']['data'];
export type RefreshResponse = OpsRefresh['responses'][200]['content']['application/json']['data'];
export type CurrentUser = OpsMe['responses'][200]['content']['application/json']['data'];

export type DashboardResponse =
  DashboardGet['responses'][200]['content']['application/json']['data'];
export type DashboardQuery = NonNullable<DashboardGet['parameters']['query']>;
export type TelemetrySummary = DashboardResponse['telemetry'];

export type LogListResponse = LogsList['responses'][200]['content']['application/json'];
export type ApplicationLog = LogListResponse['data'][number];
export type LogListQuery = NonNullable<LogsList['parameters']['query']>;

export type CrashListResponse = CrashesList['responses'][200]['content']['application/json'];
export type CrashReport = CrashListResponse['data'][number];
export type CrashListQuery = NonNullable<CrashesList['parameters']['query']>;
export type CrashGroup =
  CrashGroupsList['responses'][200]['content']['application/json']['data'][number];
export type CrashGroupQuery = NonNullable<CrashGroupsList['parameters']['query']>;

export type MfaVerifyRequest = MfaVerify['requestBody']['content']['application/json'];
export type MfaVerifyResponse = MfaVerify['responses'][200]['content']['application/json']['data'];
export type MfaStatus = MfaStatusPath['responses'][200]['content']['application/json']['data'];
export type MfaEnrollment = MfaEnroll['responses'][200]['content']['application/json']['data'];
/**
 * The MFA branch of a login response, extracted from the union rather than
 * written by hand, so a change to what the server sends is a compile error.
 */
export type MfaChallenge = Extract<LoginResponse, { status: 'mfa_required' }>;
export type StaffSession =
  SessionsList['responses'][200]['content']['application/json']['data'][number];

export type IssueListResponse = IssuesList['responses'][200]['content']['application/json'];
export type PanelIssue = IssueListResponse['data'][number];
export type IssueListQuery = NonNullable<IssuesList['parameters']['query']>;
export type RaiseIssueRequest = IssueRaise['requestBody']['content']['application/json'];
export type UpdateIssueRequest = NonNullable<
  IssueUpdate['requestBody']
>['content']['application/json'];

export type VersionListResponse = VersionsList['responses'][200]['content']['application/json'];
export type SoftwareVersion = VersionListResponse['data'][number];
export type VersionListQuery = NonNullable<VersionsList['parameters']['query']>;
export type PublishVersionRequest = VersionPublish['requestBody']['content']['application/json'];
export type UpdateVersionRequest = VersionUpdate['requestBody']['content']['application/json'];
export type VersionAdoption =
  VersionAdoptionGet['responses'][200]['content']['application/json']['data'][number];

export type DeploymentListResponse =
  DeploymentsList['responses'][200]['content']['application/json'];
export type Deployment = DeploymentListResponse['data'][number];
export type DeploymentListQuery = NonNullable<DeploymentsList['parameters']['query']>;
export type StartDeploymentRequest = DeploymentStart['requestBody']['content']['application/json'];
export type AdvanceDeploymentRequest = NonNullable<
  DeploymentAdvance['requestBody']
>['content']['application/json'];

export type OpsSettings = OpsSettingsGet['responses'][200]['content']['application/json']['data'];
export type UpdateOpsSettingsRequest = NonNullable<
  OpsSettingsPut['requestBody']
>['content']['application/json'];

/**
 * Roles that may write on this surface.
 *
 * The server enforces this - publishing a version and starting a deployment
 * require superadmin or engineering, and anything else gets a 403. It is
 * mirrored here only so the UI can disable a control rather than let somebody
 * fill in a form and discover the refusal on submit.
 *
 * Never treat this as the check. It is a courtesy, and a stale copy of a rule
 * that lives on the server.
 */
export const WRITE_ROLES = ['superadmin', 'engineering'] as const;

export const canRelease = (role: string | undefined): boolean =>
  role !== undefined && (WRITE_ROLES as readonly string[]).includes(role);

/** The failure envelope every endpoint shares. */
export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
    requestId: string;
  };
}

/** Validation failures arrive keyed by field, ready to bind to a form. */
export type FieldErrors = Record<string, string[]>;
